/**
 * Comedian booking history for the offer flow (spot picker context).
 * @see docs/APP_UI_SPEC.md — B3
 */

import {
  effectiveBringerForBooking,
  normalizeSpotKey,
  resolveSpotPlaceholders,
} from './bookingSpot.js';
import { APPLICATION_STATUSES } from './applicationStatus.js';

/** @typedef {{
 *   id: number,
 *   status: string,
 *   offer_spot_code?: string|null,
 *   offer_is_bringer?: boolean|null,
 *   event_date: string,
 *   event_name?: string,
 *   venue_name?: string,
 * }} BookingHistoryRow */

export function normalizeComedianEmailForHistory(email) {
  return String(email || '').trim().toLowerCase();
}

/**
 * Flatten Supabase join rows into BookingHistoryRow (newest event_date first).
 * @param {Array<object>} rows
 * @param {{ excludeApplicationId?: number }} [opts]
 * @returns {BookingHistoryRow[]}
 */
export function mapApplicationsToBookingHistory(rows, opts = {}) {
  const excludeId = opts.excludeApplicationId;
  const mapped = (rows || [])
    .filter((r) => r && (excludeId == null || r.id !== excludeId))
    .map((r) => ({
      id: r.id,
      status: r.status,
      offer_spot_code: r.offer_spot_code ?? null,
      offer_is_bringer: r.offer_is_bringer ?? null,
      event_date: String(r.events?.event_date || '').slice(0, 10),
      event_name: r.events?.event_name || '',
      venue_name: r.events?.venues?.venue_name || '',
    }))
    .filter((r) => r.event_date);

  mapped.sort((a, b) => b.event_date.localeCompare(a.event_date));
  return mapped;
}

/** Human label for stored offer spot, e.g. "7 min" or "5 min (bringer)". */
export function formatOfferSpotLabel(spotCode, isBringer) {
  const key = normalizeSpotKey(spotCode);
  if (!key || !['5', '7', '10', 'headliner'].includes(key)) return null;
  const spot = resolveSpotPlaceholders(key);
  const bri = effectiveBringerForBooking(key, isBringer);
  const bringerSuffix = bri ? ' (bringer)' : '';
  return `${spot.spot_label_short}${bringerSuffix}`;
}

/**
 * Most recent row that recorded an offer spot (for mismatch warnings).
 * @param {BookingHistoryRow[]} history
 * @returns {BookingHistoryRow|null}
 */
export function findMostRecentRecordedSpot(history) {
  for (const row of history || []) {
    if (row.offer_spot_code) return row;
  }
  return null;
}

/**
 * Warn when offering a shorter spot than their last recorded booking (e.g. 7 → 5 br).
 * @param {BookingHistoryRow|null} mostRecent
 * @param {{ spotCode: string, isBringer?: boolean }} pick
 * @returns {string|null}
 */
export function spotOfferMismatchWarning(mostRecent, pick) {
  if (!mostRecent?.offer_spot_code || !pick?.spotCode) return null;

  const prevKey = normalizeSpotKey(mostRecent.offer_spot_code);
  const nextKey = normalizeSpotKey(pick.spotCode);
  const rank = { 5: 1, 7: 2, 10: 3, headliner: 4 };
  const prevRank = rank[prevKey] ?? 0;
  const nextRank = rank[nextKey] ?? 0;

  if (prevRank <= 1 || nextRank >= prevRank) return null;

  const prevLabel = formatOfferSpotLabel(mostRecent.offer_spot_code, mostRecent.offer_is_bringer);
  const nextLabel = formatOfferSpotLabel(pick.spotCode, pick.isBringer);
  if (!prevLabel || !nextLabel) return null;

  return `Last recorded booking for this comic was ${prevLabel}. You are offering ${nextLabel}.`;
}

function escapeHtml(text) {
  return String(text || '')
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;');
}

function formatHistoryDateYmd(ymd) {
  if (!ymd) return '—';
  const [y, m, d] = ymd.split('-').map(Number);
  const dt = new Date(Date.UTC(y, m - 1, d));
  return dt.toLocaleDateString('en-GB', {
    weekday: 'short',
    day: 'numeric',
    month: 'short',
    year: 'numeric',
  });
}

/**
 * HTML block for SweetAlert booking dialogs (recent offers/confirmations).
 * @param {BookingHistoryRow[]} history
 * @param {string} [comedianName]
 * @returns {string}
 */
export function buildBookingHistoryContextHtml(history, comedianName) {
  const rows = history || [];
  if (rows.length === 0) {
    return `<div class="booking-history-block booking-history-block--empty"><p class="booking-history-title">Booking history (app)</p><p class="booking-history-empty">No prior offers or confirmations in the database for this email.</p></div>`;
  }

  const mostRecent = findMostRecentRecordedSpot(rows);
  const recentSpotLine = mostRecent
    ? `<p class="booking-history-recent"><strong>Most recent spot length:</strong> ${escapeHtml(formatOfferSpotLabel(mostRecent.offer_spot_code, mostRecent.offer_is_bringer) || 'not recorded')} <span class="booking-history-recent-meta">(${escapeHtml(formatHistoryDateYmd(mostRecent.event_date))}${mostRecent.venue_name ? ` · ${escapeHtml(mostRecent.venue_name)}` : ''})</span></p>`
    : `<p class="booking-history-recent booking-history-recent--muted"><strong>Spot length:</strong> not recorded for prior bookings (offers before this update). Dates below are from application status only.</p>`;

  const listItems = rows
    .slice(0, 6)
    .map((row) => {
      const spot = row.offer_spot_code
        ? formatOfferSpotLabel(row.offer_spot_code, row.offer_is_bringer)
        : 'spot not recorded';
      const status = String(row.status || '').toLowerCase();
      return `<li>${escapeHtml(formatHistoryDateYmd(row.event_date))}${row.venue_name ? ` · ${escapeHtml(row.venue_name)}` : ''} — <span class="booking-history-spot">${escapeHtml(spot)}</span> <span class="booking-history-status">(${escapeHtml(status)})</span></li>`;
    })
    .join('');

  const nameBit = comedianName ? ` for ${escapeHtml(comedianName)}` : '';
  return `<div class="booking-history-block"><p class="booking-history-title">Recent bookings${nameBit} (database)</p>${recentSpotLine}<ul class="booking-history-list">${listItems}</ul></div>`;
}

export function buildSpotMismatchWarningHtml(warning) {
  if (!warning) return '';
  return `<p class="booking-spot-mismatch-warn" role="status">${escapeHtml(warning)}</p>`;
}

const OFFER_HISTORY_STATUSES = [APPLICATION_STATUSES.OFFERED, APPLICATION_STATUSES.CONFIRMED];

/**
 * Load offered/confirmed applications for spot-picker context.
 * @param {import('@supabase/supabase-js').SupabaseClient} supabase
 * @param {string} email
 * @param {{ excludeApplicationId?: number, limit?: number }} [opts]
 */
export async function fetchComedianBookingHistoryForOffer(supabase, email, opts = {}) {
  const normalized = normalizeComedianEmailForHistory(email);
  if (!normalized) return [];

  const { data, error } = await supabase
    .from('applications')
    .select(
      'id, status, offer_spot_code, offer_is_bringer, events(event_date, event_name, venues(venue_name))'
    )
    .ilike('email', normalized)
    .in('status', OFFER_HISTORY_STATUSES);

  if (error) throw error;

  const mapped = mapApplicationsToBookingHistory(data || [], {
    excludeApplicationId: opts.excludeApplicationId,
  });
  const limit = opts.limit ?? 12;
  return mapped.slice(0, limit);
}

/**
 * Persist spot choice when the booking offer is sent (B3 / B4).
 * @param {import('@supabase/supabase-js').SupabaseClient} supabase
 * @param {number} applicationId
 * @param {{ spotCode: string, isBringer?: boolean }} spot
 * @param {string|null} [offeredByEmail] logged-in booker; stored separately so a missing column does not undo the spot
 */
export async function persistApplicationOfferSpot(supabase, applicationId, spot, offeredByEmail) {
  const offer_spot_code = normalizeSpotKey(spot.spotCode);
  const offer_is_bringer = effectiveBringerForBooking(spot.spotCode, spot.isBringer);
  const { error } = await supabase
    .from('applications')
    .update({
      offer_spot_code: ['5', '7', '10', 'headliner'].includes(offer_spot_code)
        ? offer_spot_code
        : null,
      offer_is_bringer,
    })
    .eq('id', applicationId);
  if (error) throw error;

  const booker = String(offeredByEmail || '').trim();
  if (!booker) return;
  const { error: bookerError } = await supabase
    .from('applications')
    .update({
      offered_by_email: booker,
      offered_at: new Date().toISOString(),
    })
    .eq('id', applicationId);
  if (bookerError) {
    console.warn('persistApplicationOfferSpot booker failed', bookerError);
  }
}
